<template>
	<div>
		<modal-dialog
			:modal-visible="visible"
			:title="viewOnly ? 'View Time Log' : 'Edit Time Log'"
			:submit-visible="submitVisible"
			:cancel-button-text="viewOnly || !hasModulePermWrite('TimeLogs') ? 'Done' : 'Cancel'"
			confirm-button-text="Save"
			modal-size="modal-lg"
			v-on:confirm="save"
			v-on:closeModal="closeModal">

			<div class="row">

				<div class="col-md-6">

					<h3>Details</h3>

					<table class="table table-sm">
						<tbody>
							<tr v-if="viewOnly">
								<th>Project/Service</th>
								<td v-if="record.Job">{{ record.Job.job_num }}: {{ record.Job.job_name }}</td>
								<td v-else-if="record.Service">{{ record.Service.job_num }}: {{ record.Service.job_name }}</td>
								<td v-else></td>
							</tr>
							<tr v-if="viewOnly">
								<th>Employee</th>
								<td>{{ record.User.first_name }} {{ record.User.last_name }}</td>
							</tr>
							<tr>
								<th>Time In</th>
								<td>{{ formatDateTimeToLocal(record.time_in) }}</td>
							</tr>
							<tr v-if="record.time_in_notes">
								<th>Clock In Remarks</th>
								<td>{{record.time_in_notes}}</td>
							</tr>
							<tr>
								<th>Time On Site</th>
								<td>{{ formatDateTimeToLocal(record.time_on_site,'time') }}</td>
							</tr>
							<tr>
								<th>Time Out</th>
								<td>{{ formatDateTimeToLocal(record.time_out,'time') }}</td>
							</tr>
							<tr v-if="record.time_out_notes">
								<th>Clock Out Remarks</th>
								<td>{{record.time_out_notes}}</td>
							</tr>
							<tr>
								<th>Time Diff</th>
								<td>{{inOutDiffHrs}}</td>
							</tr>
							<tr>
								<th>Clocked In Onsite</th>
								<td v-if="record.no_location_clockin == 1" class="text-warning">
									<i class="fa fa-warning"></i>
									No location captured during clock-in
								</td>
								<td v-else v-html="stripInlineEventHandlers(onsiteFlags.wasOnsite)"></td>
							</tr>
							<tr>
								<th>Clocked Out Onsite</th>
								<td v-if="record.no_location_clockout == 1" class="text-warning">
									<i class="fa fa-warning"></i>
									No location captured during clock-out
								</td>
								<td v-else v-html="stripInlineEventHandlers(onsiteFlags.wasOnsiteClockout)"></td>
							</tr>
							<tr v-if="!onsiteFlags.onsiteBoolean">
								<th>Reason Not On Site: ClockIn</th>
								<td>{{ record.reason_not_on_site }}</td>
							</tr>
							<tr v-if="record.PayBoost && record.PayBoost.length > 0 && viewOnly">
								<th>Pay Adjustments Claimed</th>
								<td>
									<span v-for="(pb,index) in record.PayBoost">
										{{pb.description}}<template v-if="index + 1 < record.PayBoost.length">, </template>
									</span>
								</td>
							</tr>
							
						</tbody>
					</table>

					<template v-if="viewOnly">

						<h3>Reported Hours</h3>
						<table class="table table-sm">
							<tbody>
								<tr>
									<th>Regular Hours</th>
									<td class="text-right">{{ record.regular_hours }} <div v-if="record.regular_hours_original != record.regular_hours && record.regular_hours_original > 0" class="text-muted">(originally claimed {{record.regular_hours_original}} hrs)</div></td>
								</tr>
								<tr v-if="record.overtime_hours_15x > 0">
									<th>Overtime @ 1.5x</th>
									<td class="text-right">{{ record.overtime_hours_15x }} <div v-if="record.overtime_hours_15x_original != record.overtime_hours_15x && record.overtime_hours_15x_original > 0" class="text-muted">(originally claimed {{record.overtime_hours_15x_original}} hrs)</div></td>
								</tr>
								<tr v-if="record.overtime_hours_2x > 0">
									<th>Overtime @ 2x</th>
									<td class="text-right">{{ record.overtime_hours_2x }} <div v-if="record.overtime_hours_2x_original != record.overtime_hours_2x && record.overtime_hours_2x_original > 0" class="text-muted">(originally claimed {{record.overtime_hours_2x_original}} hrs)</div></td>
								</tr>
								<tr v-if="record.overtime_hours_25x > 0">
									<th>Overtime @ 2.5x</th>
									<td class="text-right">{{ record.overtime_hours_25x }} <div v-if="record.overtime_hours_25x_original != record.overtime_hours_25x && record.overtime_hours_25x_original > 0" class="text-muted">(originally claimed {{record.overtime_hours_25x_original}} hrs)</div></td>
								</tr>
							</tbody>
						</table>

					</template>
					<template v-else>

						<form-group label="Employee" col-class="col-md-4">
							<div v-if="!allowUserEdit"  class="form-control-plaintext">
								{{'User' in record ? record.User.first_name : ''}} {{'User' in record? record.User.last_name : ''}}
							</div>
							<field v-else 
								v-model="payload.user_id" 
								:params="{type:'select',options:userOptions(false)}">
							</field>
						</form-group>

						<form-group v-if="!allowJobEdit" label="Project/Service" col-class="col-md-4">
							<div class="form-control-plaintext">
								{{jobDisplay}}
							</div>
						</form-group>

						<template v-else>
							<job-picker
								v-if="payload.service_id == 0 || payload.service_id == 'null' || !payload.service_id"
								col-class="col-md-4" 
								job-type="project"
								key="project"
								v-model="payload.job_id"
								v-on:change="jobChange">
								<a :href="jobLink" target="_blank">{{ jobLinkText }}</a>
							</job-picker>

							<job-picker
								v-if="payload.job_id == 0 || payload.job_id == 'null' || !payload.job_id"
								col-class="col-md-4" 
								job-type="service"
								key="service"
								v-model="payload.service_id"
								v-on:change="jobChange">
								<a :href="jobLink" target="_blank">{{ jobLinkText }}</a>
							</job-picker>

						</template>

						<field v-if="payBoostOptions.length > 0" 
							label="Pay Adjustments Applied" 
							col-class="col-md-4" 
							:params="{type:'select-multi',options:payBoostOptions}" 
							v-model="payload.PayBoost">
						</field>

						<field label="Regular Hours" col-class="col-md-4" v-model="payload.regular_hours" :params="{type:'number',min:0,max:24,step:0.25}">
							<span v-if="payload.regular_hours != record.regular_hours_original" class="text-muted">
								Original: {{record.regular_hours_original}} hrs
							</span>
						</field>

						<field v-if="timeLogSettingsLocal.uses_overtime_15x == 1 || timeLogSettingsLocal" label="Overtime x1.5" col-class="col-md-4" v-model="payload.overtime_hours_15x" :params="{type: 'number',min:0,max:24,step:0.25}">
							<span v-if="payload.overtime_hours_15x != record.overtime_hours_15x_original" class="text-muted">
								Original: {{record.overtime_hours_15x_original}} hrs
							</span>
						</field>

						<field v-if="timeLogSettingsLocal.uses_overtime_2x == 1 || !timeLogSettingsLocal" label="Overtime x2.0" col-class="col-md-4" v-model="payload.overtime_hours_2x" :params="{type: 'number',min:0,max:24,step:0.25}">
							<span v-if="payload.overtime_hours_2x != record.overtime_hours_2x_original" class="text-muted">
								Original: {{record.overtime_hours_2x_original}} hrs
							</span>
						</field>

						<field v-if="timeLogSettingsLocal.uses_overtime_25x == 1 || !timeLogSettingsLocal" label="Overtime x2.5" col-class="col-md-4" v-model="payload.overtime_hours_25x" :params="{type: 'number',min:0,max:24,step:0.25}">
							<span v-if="payload.overtime_hours_25x != record.overtime_hours_25x_original" class="text-muted">
								Original: {{record.overtime_hours_25x_original}} hrs
							</span>
						</field>

						<template v-if="showSkipMileagePerDiem">
							<field v-if="job && job.pays_mileage == 1"
								label="Skip Mileage Pay"
								col-class="col-md-4"
								v-model="payload.skip_mileage"
								:params="{type:'boolean'}">
							</field>

							<field v-if="job && job.pays_perdiem == 1"
								label="Skip Per Diem Pay"
								col-class="col-md-4"
								v-model="payload.skip_perdiem"
								:params="{type:'boolean'}">
							</field>
						</template>
					</template>
				</div>
				<div class="col-md-6">

					<h3>Location</h3>

					<!--<form-group label="Clock In/Out Location" col-class="col-md-4">
						<ul>
							<li v-if="typeof clockInDistance == 'number'">Clocked in <strong>{{clockInDistance}} km</strong> from Job</li>
							<li v-else>No clockout location recorded</li>

							<li v-if="typeof clockOutDistance == 'number'">Clocked out <strong>{{clockOutDistance}} km</strong> from Job</li>
							<li v-else>No clockout location recorded</li>
						</ul>

						

					</form-group>-->

					<table class="table table-sm">
						<tbody>
							<tr>
								<th>Clock IN Position</th>
								<td v-if="typeof clockInDistance == 'number' && payload.lat != 0 && payload.long != 0">
									Clocked in <strong>{{clockInDistance}} km</strong> 
									from Job 
									(<strong>&plusmn; {{record.position_accuracy}}m</strong>)
								</td>	
								<td v-else>Position not recorded</td>
							</tr>
							<tr>
								<th>Clock OUT Position</th>
								<td v-if="typeof clockOutDistance == 'number' && payload.lat_clockout != 0 && payload.long_clockout != 0">
									Clocked out <strong>{{clockOutDistance}} km</strong>
									from Job
									(<strong>&plusmn; {{record.position_accuracy_clockout}}m</strong>)
								</td>	
								<td v-else>Position not recorded</td>
							</tr>
						</tbody>
					</table>

					<p><a v-if="!showClockInMap" href="#" v-on:click.prevent="showClockInMap = true">
						<i class="fa fa-map"></i>
						View Map
					</a></p>

					<vue-map
						class="mb-3"
						v-if="showClockInMap"
						:center="mapCenter"
						:zoom="mapZoom"
						map-size="map-sm"
						:markers="logMarkers"
						:circles="mapRangeCircles">
					</vue-map>

					<field v-if="!viewOnly"
						label="Review Notes" 
						col-class="col-md-4" 
						v-model="payload.review_notes" 
						:params="{'type': 'textarea',size:'xsm'}"></field>

					<h3 class="mt-2">Edit/Review History</h3>

					<table class="table table-sm">
						<tbody>
							<tr>
								<th>Reviewed</th>
								<td>
									<span v-if="record.ReviewedBy">{{ record.ReviewedBy.first_name }} {{ record.ReviewedBy.last_name }}</span>
									<i v-else class="fa fa-times"></i>
								</td>
							</tr>
							<tr>
								<th>Submitted</th>
								<td>
									<span v-if="record.SubmittedBy">{{ record.SubmittedBy.first_name }} {{ record.SubmittedBy.last_name }}</span>
									<i v-else class="fa fa-times"></i>
								</td>
							</tr>
							<tr>
								<th>Last Edit</th>
								<td>
									<span v-if="record.UpdatedBy && record.created_at != record.updated_at">{{ record.UpdatedBy.first_name }} {{ record.UpdatedBy.last_name }} @ {{ formatDateTimeToLocal(record.updated_at) }}</span>
									<span v-else class="text-muted">Original Record</span>
								</td>
							</tr>
							<tr v-if="record.review_notes && viewOnly">
								<th>Review Notes</th>
								<td>{{record.review_notes}}</td>
							</tr>
						</tbody>
					</table>

					<field v-if="showReviewedButton && logValidToBeReviewed"
						v-model="reviewed_by"
						v-on:change="saveReviewedBy"
						:params="{type:'btn-group',buttons:[
							{text:'Reviewed',icon:'fa-check',value:$store.userDetails.id},
							{icon: 'fa-times',value:0}
						]}">
					</field>

					<div v-else-if="showReviewedButton && !logValidToBeReviewed" class="text-muted mt-2">
						The log can't be marked as 'reviewed' until it has hours claimed, and is assigned to both a Service/Project and User
					</div>

					<comment-thread v-if="hasComments"
						:parent-slug="record.slug"
						:thread-id="'message_thread_id' in record ? record.message_thread_id : false"
						parent-model="TimeLog"
						:thread-subject-reference="threadSubjectRef">
					</comment-thread>
				</div>
			</div>
			<div class="row">
				<div class="col-md-12">
					<div class="d-flex align-items-center mb-2">
						<div v-if="record && 'Job' in record && record.Job && 'detailed_timelogs' in record.Job && record.Job.detailed_timelogs == 1 && !showDetailedClaims" >
							<a href="#" v-on:click.prevent="showDetailedClaims = true">Show Detailed Claims</a>
						</div>
					</div>

					<div v-if="showDetailedClaims">
						<div class="d-flex justify-content-between align-items-center mb-2">
							<h3 class="mb-0">Detailed Claims</h3>
							<div>
								<i v-if="timeLogDetailedClaims.length > 0" class="fa fa-trash icon-button text-danger mr-4" title="Delete detailed claims" v-on:click="deleteDetailedClaims"></i>
								<i class="fa fa-times icon-button" v-on:click="showDetailedClaims = false"></i>
							</div>
						</div>

						<button v-if="payload.job_id > 0 && !showDetailedLogAllocator && timeLogDetailedClaims.length == 0" class="btn btn-sm btn-light mr-4" type="button" v-on:click="allocateDetailedHours">Add Detailed Claims...</button>

						<widget v-if="showDetailedLogAllocator" title="Add Detailed Claims">
							<detailed-time-allocator 
								v-model="reportedComponentWork" 
								:project="project" 
								:log="record">
							</detailed-time-allocator>

							<button v-if="reportedComponentWork.length > 0 && reportedDetailedClaimsMatchTotalClaimedHours" class="btn btn-sm btn-primary" type="button" v-on:click="saveDetailedClaims">
								Save Detailed Claims
							</button>
						</widget>

						<table class="table table-sm" >
							<thead>
								<tr>
									<th>Component/Change Order</th>
									<th class="text-right">Reg. Hrs</th>
									<th v-if="$store.timeLogSettings.uses_overtime_15x == 1" class="text-right">OT 1.5x</th>
									<th v-if="$store.timeLogSettings.uses_overtime_2x == 1" class="text-right">OT 2x</th>
									<th v-if="$store.timeLogSettings.uses_overtime_25x == 1" class="text-right">OT 2.5x</th>
									<th v-if="!viewOnly"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="claim in timeLogDetailedClaims">
									<td v-if="'JobBidComponent' in claim && claim.JobBidComponent">{{ claim.JobBidComponent.item_number }}: {{ claim.JobBidComponent.name }}</td>
									<td v-else-if="'JobChangeOrder' in claim && claim.JobChangeOrder">CO-{{ record.Job.job_num }}-{{ claim.JobChangeOrder.quote_num }}</td>
									<td class="text-right">{{ claim.regular_hours }}</td>
									<td v-if="$store.timeLogSettings.uses_overtime_15x == 1" class="text-right">{{ claim.overtime_hours_15x }}</td>
									<td v-if="$store.timeLogSettings.uses_overtime_2x == 1" class="text-right">{{ claim.overtime_hours_2x }}</td>
									<td v-if="$store.timeLogSettings.uses_overtime_25x == 1" class="text-right">{{ claim.overtime_hours_25x }}</td>
									<td v-if="!viewOnly">
										<i class="fa fa-pencil-alt icon-button" v-on:click="editDetailedClaim(claim)"></i>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<template slot="footer">
				<button v-if="hasModulePermWrite('TimeLogs')" class="btn btn-outline-danger btn-sm mr-2" type="button" v-on:click="deleteTimeLog">
					<i class="fa fa-trash"></i>
					Delete
				</button>

				<template v-if="showTransferButton">
					<button v-if="payload.overtime_hours_15x > 0 || payload.overtime_hours_2x > 0 || payload.overtime_hours_15x > 0" class="btn btn-light btn-sm" type="button" v-on:click="openTransferModal"><i class="fa fa-paper-plane"></i> Transfer OT Hours...</button>
				</template>
			</template>
			
		</modal-dialog>

		<modal-dialog
			:modal-visible="deleteModal.visible"
			title="Delete Time Log"
			confirm-button-text="Delete"
			button-class="btn-outline-danger"
			v-on:confirm="executeDeleteTimeLog"
			v-on:closeModal="deleteModal.visible = false">
			Delete this time log? It will not be recoverable
		</modal-dialog>

		<modal-dialog
			:modal-visible="detailedClaimEditModal.visible"
			title="Edit Detailed Claim"
			confirm-button-text="Save"
			v-on:confirm="saveDetailedClaimEdit"
			v-on:closeModal="detailedClaimEditModal.visible = false">

			<h4>{{ detailedClaimEditModal.componentName }}</h4>

			<field
				label="Regular Hours"
				col-class="col-md-4"
				v-model="detailedClaimEditModal.payload.regular_hours"
				:params="{type:'number', step: 0.25, min: 0}">
			</field>

			<field v-if="$store.timeLogSettings.uses_overtime_15x == 1"
				label="Overtime 1.5x"
				col-class="col-md-4"
				v-model="detailedClaimEditModal.payload.overtime_hours_15x"
				:params="{type:'number', step: 0.25, min: 0}">
			</field>

			<field v-if="$store.timeLogSettings.uses_overtime_2x == 1"
				label="Overtime 2x"
				col-class="col-md-4"
				v-model="detailedClaimEditModal.payload.overtime_hours_2x"
				:params="{type:'number', step: 0.25, min: 0}">
			</field>

			<field v-if="$store.timeLogSettings.uses_overtime_25x == 1"
				label="Overtime 2.5x"
				col-class="col-md-4"
				v-model="detailedClaimEditModal.payload.overtime_hours_25x"
				:params="{type:'number', step: 0.25, min: 0}">
			</field>
			
		</modal-dialog>

		<modal-dialog :modal-visible="deleteDetailedClaimsModal.visible"
			title="Delete Detailed Claims?"
			v-on:confirm="executeDeleteDeleteDetailedClaims"
			v-on:closeModal="deleteDetailedClaimsModal.visible = false"
			confirm-button-text="Yes, Delete"
			button-class="btn-danger">
			Are you sure you want to clear all the detailed claims?
		</modal-dialog>
	</div>
</template>
<script type="text/javascript">
import ModalDialog from "../ModalDialog"
import FormGroup from "../FormGroup"
import Field from "../Field"
import JobMap from "../JobMap"
import VueMap from "../VueMap"
import CommentThread from "@/components/super_components/CommentThread"
import JobPicker from "@/components/super_components/JobPicker"
import DetailedTimeAllocator from "@/components/super_components/DetailedTimeAllocator"
import Widget from "@/components/Widget"

export default {
	name: "TimeLogEditModal",
	components: {
		ModalDialog,
		Field,
		FormGroup,
		JobMap,
		VueMap,
		CommentThread,
		JobPicker,
		DetailedTimeAllocator,
		Widget
	},
	props: [
		"visible",
		"record",
		"job",
		"showTransferButton",
		"timeLogSettings",
		"showReviewedButton",
		"allowJobEdit",
		"allowUserEdit",
		"showSkipMileagePerDiem",
		"viewOnly",
		"hasComments"
	],
	data() {
		return {
			payload: {},
			showDetailedLogAllocator: null,
			project: null,
			reviewed_by: this.record.reviewed_by,
			payBoosts: [],
			deleteModal: {
				visible: false,
			},
			detailedClaimEditModal: {
				visible: false,
				payload: {},
				componentName: "",
				working: false
			},
			showDetailedClaims: false,
			showClockInMap: false,
			timeLogSettingsLocal: this.timeLogSettings,
			timeLogDetailedClaims: null,
			reportedComponentWork: [],
			deleteDetailedClaimsModal: {
				visible: false,
				working: false
			}
		}
	},
	watch: {
		record() {
			this.fetchJobPayBoosts()
			this.setOrFetchDetailedClaims()

			console.log(this.record)
		},
		visible() {
			this.reviewed_by = this.record.reviewed_by
		},
		'payload.service_id'() {
			if (this.payload.service_id > 0)
				this.payload.job_id = null
		},
		'payload.job_id'() {
			if (this.payload.job_id > 0)
				this.payload.service_id = null
		},
		timeLogSettings() {
			this.setTimeLogSettingsLocal()
		}
	},
	computed: {
		submitVisible() { 
			if (this.viewOnly)
				return false

			if (this.showDetailedClaims && this.reportedComponentWork.length > 0 && !this.reportedDetailedClaimsMatchTotalClaimedHours)
				return false

			if (this.hasModulePermWrite('TimeLogs'))
				return true

			if (this.record.reviewed_by == 0 && this.record.user_id == this.$store.userDetails.id)
				return true

			return false
		},
		threadSubjectRef() {
			if (this.record && "User" in this.record && this.record.User && "first_name" in this.record.User && "last_name" in this.record.User)
				return "Time Log: "+this.record.User.first_name+" "+this.record.User.last_name
			else
				return "Time Log"
		},
		inOutDiffHrs() {
			let diff = this.dateDiff(this.record.time_in,this.record.time_out,'hrs')

			if (isNaN(diff))
				return "[tbd]"

			return diff +" hrs"
		},
		logValidToBeReviewed() {
			if (
				(this.payload.job_id > 0 || this.payload.service_id > 0) && 
				this.payload.user_id > 0 && 
				(this.payload.regular_hours > 0 || this.payload.overtime_hours_15x > 0 || this.payload.overtime_hours_2x > 0 || this.payload.overtime_hours_25x > 0)
				)
				return true
			else
				return false
		},
		jobDisplay() {
			let type = "Service"
			if (this.record.Job)
				type = "Job"
			else if (this.record.Service)
				type = "Service"
			else
				return "[unnassigned]"

			return this.record[type].job_num + ": " + this.record[type].job_name
		},
		payBoostOptions() {
			if (this.payBoosts)
				return this.optionify(this.payBoosts,"description")
			else
				return []
		},
		clockInDistance() { //in km

			if (parseFloat(this.record.lat) == 0 || parseFloat(this.record.long) == 0)
				return "No location recorded"

			if (Object.keys(this.record).length > 0 && Object.keys(this.jobLocation).length > 0) {
				let distance = this.getDistance(
						this.jobLocation.lat,
						this.jobLocation.lng,
						+this.record.lat,
						+this.record.long)

				if (isNaN(distance))
					return "[error]"
				else {
					distance = distance/1000

					return distance.round(1)
				}
			}
			else
				return 0
		},
		clockOutDistance() { //in km

			if (parseFloat(this.record.lat_clockout) == 0 || parseFloat(this.record.long_clockout) == 0)
				return "No location recorded"

			if (Object.keys(this.record).length > 0 && Object.keys(this.jobLocation).length > 0) {
				let distance = this.getDistance(
					this.jobLocation.lat,
					this.jobLocation.lng,
					+this.record.lat_clockout,
					+this.record.long_clockout
				)

				if (isNaN(distance))
					return "[error]"
				else {
					distance = distance/1000

					return distance.round(1)
				}
			}
			else
				return 0
		},
		mapZoom() {
			if (this.clockInDistance < 10)
				return 12
			else if (this.clockInDistance < 50)
				return 10
			else if (this.clockInDistance < 100)
				return 8
			else
				return 7
		},
		mapCenter() {
			let jobType = false
			if (this.record.Service)
				jobType = "Service"
			else if (this.record.Job)
				jobType = "Job"

			if (jobType) {
				return {
					lat: parseFloat(this.record[jobType].lat),
					lng: parseFloat(this.record[jobType].long)
				}
			}
			else {
				return {
					lat: parseFloat(this.record.lat),
					lng: parseFloat(this.record.long)
				}
			}
		},
		logMarkers() {
			let markers = []

			let jobType = false
			if (this.record.Service)
				jobType = "Service"
			else if (this.record.Job)
				jobType = "Job"

			if (jobType) {
				markers.push({
					lat: parseFloat(this.record[jobType].lat),
					lng: parseFloat(this.record[jobType].long),
					icon: window.apiRoot+"resources/icons/hardhat_36_point.png",
					infoWindowText: this.record[jobType].job_num+": "+this.record[jobType].job_name
				})
			}

			let position_icon = {
				anchor: {x: 18,y :18},
				url: window.apiRoot+"resources/icons/location_36.png"
			}

			//check distance between clock-in and clock-out, if they're under 50 metres, we'll only draw one marker

			let lat, long, lat_clockout, long_clockout

			lat = parseFloat(this.record.lat)
			long = parseFloat(this.record.long)
			lat_clockout = parseFloat(this.record.lat_clockout)
			long_clockout = parseFloat(this.record.long_clockout)

			if (lat_clockout == 0 && long_clockout == 0) {
				//just push clockin marker
				markers.push({
					lat: lat,
					lng: long,
					icon: position_icon,
					infoWindowText: "Clock In"
				})
			}
			else {
				if (this.getDistance(this.record.lat, this.record.long, this.record.lat_clockout, this.record.long_clockout) < 50) {
					//only draw a single marker for both, showing blended location (split the difference)

					let diffLat, diffLong

					if (lat > lat_clockout)
						diffLat = ((lat - lat_clockout) / 2) + lat_clockout
					else
						diffLat = ((lat_clockout - lat) / 2) + lat

					if (long > long_clockout)
						diffLong = ((long - long_clockout) / 2) + long_clockout
					else
						diffLong = ((long_clockout - long) / 2) + long

					markers.push({
						lat: diffLat,
						lng: diffLong,
						icon: position_icon,
						infoWindowText: "Clock In & Out (Same Location)"
					})
				}
				else {
					markers.push({
						lat: lat,
						lng: long,
						icon: position_icon,
						infoWindowText: "Clock In"
					})

					markers.push({
						lat: lat_clockout,
						lng: long_clockout,
						icon: position_icon,
						infoWindowText: "Clock Out"
					})
				}
			}

			return markers
		},
		mapRangeCircles() {
			let circles = []

			let jobType = false
			if (this.record.Service)
				jobType = "Service"
			else if (this.record.Job)
				jobType = "Job"

			if (jobType) {
				circles.push({
					lat: parseFloat(this.record[jobType].lat),
					lng: parseFloat(this.record[jobType].long),
					radius: this.record[jobType].check_in_tolerance,
					strokeColor: '#ffc107',
					fillColor: '#ffc1074f'
				})
			}

			let position_icon = {
				anchor: {x: 18,y :18},
				url: window.apiRoot+"resources/icons/location_36.png"
			}

			circles.push({
				lat: parseFloat(this.record.lat),
				lng: parseFloat(this.record.long),
				radius: parseFloat(this.record.position_accuracy),
				strokeColor: '#07adff',
				fillColor: '#07adff3b'
			})

			circles.push({
				lat: parseFloat(this.record.lat_clockout),
				lng: parseFloat(this.record.long_clockout),
				radius: parseFloat(this.record.position_accuracy_clockout),
				strokeColor: '#07adff',
				fillColor: '#07adff3b'
			})

			return circles
		},
		jobLocation() {

			if (this.record.Job) {
				return {
					lat: parseFloat(this.record.Job.lat),
					lng: parseFloat(this.record.Job.long)
				}
			}
			else if (this.record.Service) {
				return {
					lat: parseFloat(this.record.Service.lat),
					lng: parseFloat(this.record.Service.long)
				}
			}
			else {
				return false
			}
		},
		reportedDetailedClaimsMatchTotalClaimedHours() {
			let regTotal = this.reportedComponentWork.reduce((acc, item) => {
				if ("regular_hours" in item)
					return acc + +item.regular_hours
				return acc
			},0)
			let ot15Total = this.reportedComponentWork.reduce((acc, item) => {
				if ("overtime_hours_15x" in item)
					return acc + +item.overtime_hours_15x
				return acc
			},0)
			let ot2Total = this.reportedComponentWork.reduce((acc, item) => {
				if ("overtime_hours_2x" in item)
					return acc + +item.overtime_hours_2x
				return acc
			},0)
			let ot25Total = this.reportedComponentWork.reduce((acc, item) => {
				if ("overtime_hours_25x" in item)
					return acc + +item.overtime_hours_25x
				return acc
			},0)

			if (regTotal == +this.record.regular_hours && ot15Total == +this.record.overtime_hours_15x && ot2Total == +this.record.overtime_hours_2x && ot25Total == +this.record.overtime_hours_25x)
				return true
			else
				return false
		},
		jobLinkText() {
			
			if (this.record) {
				let jobKey = false, jobType = false
				if ("Job" in this.record && this.record.Job) {
					jobKey = "Job"
					jobType = "Project"
				}
				
				else if ("Service" in this.record && this.record.Service) {
					jobKey = "Service"
					jobType = "Service"
				}
				
				if (jobKey)
					return jobType + "# " + this.record[jobKey].job_num
			}
			return ""
		},
		jobLink() {
			if (this.record) {
				let jobKey = false, jobType = false
				if ("Job" in this.record && this.record.Job) 
					return "/project/" + this.record.Job.slug + "/notes"
				
				else if ("Service" in this.record && this.record.Service)
					return "/service/" + this.record.Service.slug + "/worksheet"
					
			}
			return ""
		},
		onsiteFlags() {
			let flags = {}, distance = 0

			let jobKey = false
			if ("Job" in this.record && this.record.Job) {
				jobKey = "Job"
			}
			
			else if ("Service" in this.record && this.record.Service) {
				jobKey = "Service"
			}

			if (jobKey in this.record && this.record[jobKey]) {

				if (this.record .lat != 0 && this.record .long != 0) {
					distance = this.getDistance(this.record[jobKey].lat,this.record[jobKey].long,this.record .lat,this.record .long)

					if (distance > this.record[jobKey].check_in_tolerance) {
						flags.onsiteBoolean = false
						flags.wasOnsite = "<i class='fa fa-times'></i>";
					}
					else {
						flags.onsiteBoolean = false
						flags.wasOnsite = "<i class='fa fa-check'></i>";
					}
				}

				if (this.record.lat_clockout != 0 && this.record.long_clockout != 0) {
					distance = this.getDistance(this.record[jobKey].lat,this.record[jobKey].long,this.record.lat_clockout,this.record.long_clockout)
					if (distance > this.record[jobKey].check_in_tolerance) {
						flags.onsiteClockoutBoolean = false
						flags.wasOnsiteClockout = "<i class='fa fa-times'></i>";
					}
					else{
						flags.onsiteClockoutBoolean = false
						flags.wasOnsiteClockout = "<i class='fa fa-check'></i>";
					}
				}
			}
		
			return flags 
		}
	},
	methods: {
		setTimeLogSettingsLocal() {
			if (this.timeLogSettings)
				this.timeLogSettingsLocal = this.timeLogSettings
			else {
				this.timeLogSettingsLocal = {
					uses_overtime_15x: 1,
					uses_overtime_2x: 1,
					uses_overtime_25x: 1
				}
			}
		},
		setOrFetchDetailedClaims() {
			if (this.record && "TimeLogDetailedClaim" in this.record)
				this.timeLogDetailedClaims = this.record.TimeLogDetailedClaim
			else if (this.record && "id" in this.record)
				this.fetchLogDetailedClaims(this.record.id)
		},
		closeModal() {
			this.$emit("closeModal")
		},
		save() {
			if (this.showDetailedLogAllocator) {
				this.saveDetailedClaims().then(response => {
					if (response.success)
						this.$emit("save",this.payload)
				})
			}
			else
				this.$emit("save",this.payload)
		},
		openTransferModal() {
			this.$emit("openTransferModal",this.payload)
		},
		saveReviewedBy() {
			let payload = {
				id: this.record.id,
				reviewed_by: this.reviewed_by
			}

			let vm = this

			this.saveRecord("TimeLog",payload,response => {
				vm.alertSaved("Time Log's reviewed status has been updated")
				vm.$emit("reviewStatusChange",vm.record,vm.reviewed_by)
				vm.$bus.$emit("reviewStatusChange",vm.record,vm.reviewed_by)
				vm.$emit("closeModal")
			})
		},
		deleteTimeLog() {
			this.deleteModal.visible = true
		},
		executeDeleteTimeLog() {
			this.deleteModal.visible = false
			let vm = this

			this.postData("TimeLog/delete",{id: this.record.id},true).then(response => {
				if (response.success) {
					vm.alertSuccess("Time Log deleted")
					vm.$emit("closeModal")
					vm.$emit("modelChanged")
				}
			})
		},
		editDetailedClaim(claim) {
			this.detailedClaimEditModal.visible = true
			this.detailedClaimEditModal.componentName = claim.JobBidComponent.item_number + ": " + claim.JobBidComponent.name
			this.detailedClaimEditModal.payload = {
				id: claim.id,
				regular_hours: claim.regular_hours, 
				overtime_hours_15x: claim.overtime_hours_15x,
				overtime_hours_2x: claim.overtime_hours_2x,
				overtime_hours_25x: claim.overtime_hours_25x
			}
		},
		allocateDetailedHours() {
			let vm = this

			if (!this.project) {
				this.getJSON("Job/find/" + this.payload.job_id,true).then(response => {
					if (response.success) {
						vm.project = response.data
						this.showDetailedLogAllocator = true
					}
				})
			}
			else
				this.showDetailedLogAllocator = true
		},
		deleteDetailedClaims() {
			this.deleteDetailedClaimsModal.visible = true
		},
		executeDeleteDeleteDetailedClaims() {
			let vm = this

			this.deleteDetailedClaimsModal.working = true

			let promises = []
			this.timeLogDetailedClaims.forEach(claim => {
				promises.push(this.postData("TimeLogDetailedClaim/delete",{id: claim.id}))
			})

			Promise.all(promises).then(responses => {
				this.deleteDetailedClaimsModal.visible = false
				this.deleteDetailedClaimsModal.working = false
				this.fetchLogDetailedClaims()
			})
		},
		saveDetailedClaims() {
			let vm = this
			//save the detailed work claims
			let payload = {
				records: JSON.stringify(vm.reportedComponentWork.map(item => {

					let payloadObj = {
						timelog_id: vm.record.id,
						regular_hours: item.regular_hours,
						overtime_hours_15x: item.overtime_hours_15x,
						overtime_hours_2x: item.overtime_hours_2x,
						overtime_hours_25x: item.overtime_hours_25x
					}

					if ("component_id" in item)
						payloadObj.job_bid_component_id = item.component_id

					if ("change_quote_id" in item)
						payloadObj.change_quote_id = item.change_quote_id

					return payloadObj
				}))
			}

			return new Promise((resolve,reject) =>{

				vm.postData("TimeLogDetailedClaim/save-batch",payload).then(response => {
					if (response.success) {
						vm.alertSaved("Detailed claims saved")
						vm.showDetailedLogAllocator = false
						vm.fetchLogDetailedClaims(vm.record.id)
					}
					resolve(response)
				})
			})
		},
		saveDetailedClaimEdit() {
			let vm = this

			this.detailedClaimEditModal.working = true

			this.postData("TimeLogDetailedClaim/save",this.detailedClaimEditModal.payload,true).then(response => {
				this.detailedClaimEditModal.working = false

				if (response.success) {
					vm.alertSaved("Detailed claim edit saved")
					vm.detailedClaimEditModal.visible = false
					vm.detailedClaimEditModal.payload = {}
					vm.fetchLogDetailedClaims(vm.record.id)
				}
			})
		},
		jobChange(value) {
			this.$forceUpdate()
		},
		fetchJobPayBoosts() {
			let vm = this

			let params = {}

			if (this.record.job_id > 0) {
				params.include = ["Job"]
				params.filters = [["Job.id",this.record.job_id]]
			}
			else if (this.record.service_id > 0) {
				params.include = ["Service"]
				params.filters = [["Service.id",this.record.service_id]]	
			}

			this.getJSON("PayBoost/get",params).then(response => {
				vm.payBoosts = response.data

				vm.payload = {
					id: vm.record.id,
					user_id: vm.record.user_id,
					job_id: vm.record.job_id,
					service_id: vm.record.service_id,
					tenant_id: vm.record.tenant_id,	
					regular_hours: vm.record.regular_hours,
					overtime_hours_15x: vm.record.overtime_hours_15x,
					overtime_hours_2x: vm.record.overtime_hours_2x,
					overtime_hours_25x: vm.record.overtime_hours_25x,
					review_notes: vm.record.review_notes,
					skip_mileage: vm.record.skip_mileage,
					skip_perdiem: vm.record.skip_perdiem,
					PayBoost: "PayBoost" in vm.record && vm.record.PayBoost ? vm.primitize(vm.record.PayBoost) : []
				}

				vm.reviewed_by = vm.record.reviewed_by
			})
		},
		fetchLogDetailedClaims(logId) {
			let vm = this

			let params = {
				filters: [
					["timelog_id",logId]
				],
				include: ["JobBidComponent","JobChangeOrder"]
			}

			this.getJSON("TimeLogDetailedClaim/get",params).then(response => {
				if (response.success) {
					vm.timeLogDetailedClaims = response.data
				}
			})
		}
	},
	created() {
		this.reviewed_by = this.record.reviewed_by

		this.setTimeLogSettingsLocal()
		this.fetchJobPayBoosts()

		this.setOrFetchDetailedClaims()		

		console.log(this.record)
	}

}
</script>